import { createFileRoute, useMatchRoute, useNavigate } from '@tanstack/react-router'
import { AnalyticsExclusionsPanel } from '@workspace/seedforge-features/settings'
import type {
  SettingsInvite,
  SettingsNotification,
  SettingsUser,
} from '@workspace/seedforge-ui/settings'
import { SettingsView } from '@workspace/seedforge-ui/settings'
import { cn } from '@workspace/ui/lib/utils'
import { BarChart3, Users, Wallet } from 'lucide-react'
import { FounderLayout } from '../../../../components/founder-layout'
import { useStartupContext } from '../../../../contexts/startup-guard'
import { BillingPageContent } from '../../../../features/billing/components/billing-page-content'
import { InvestorVisibilitySection } from '../../../../features/stealth-choice/components/investor-visibility-section'
import {
  useAnalyticsSettingsQuery,
  useCancelInviteMutation,
  useCreateAnalyticsExclusionMutation,
  useCreateInviteMutation,
  useDeleteAnalyticsExclusionMutation,
  useRemoveStartupMemberMutation,
  useStartupInvitesQuery,
  useStartupMembersQuery,
} from '../../../../hooks/api'
import { useBasePath } from '../../../../hooks/use-base-path'

export const Route = createFileRoute('/app/startup/$id/settings')({
  component: FounderSettingsPage,
})

type SettingsTab = 'general' | 'analytics' | 'billing'

const TAB_ROUTES: Record<SettingsTab, string> = {
  general: '/app/startup/$id/settings',
  analytics: '/app/startup/$id/settings/analytics',
  billing: '/app/startup/$id/settings/billing',
}

const TABS: { key: SettingsTab; label: string; icon: React.ElementType }[] = [
  { key: 'general', label: 'General', icon: Users },
  { key: 'analytics', label: 'Analytics', icon: BarChart3 },
  { key: 'billing', label: 'Billing', icon: Wallet },
]

function useActiveTab(): SettingsTab {
  const matchRoute = useMatchRoute()
  const params = Route.useParams()
  if (matchRoute({ to: '/app/startup/$id/settings/analytics', params })) return 'analytics'
  if (matchRoute({ to: '/app/startup/$id/settings/billing', params })) return 'billing'
  return 'general'
}

function getInitials(name: string | null, email: string | null): string {
  if (name) {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return email?.charAt(0).toUpperCase() ?? '?'
}

const FOUNDER_NOTIFICATIONS: SettingsNotification[] = [
  {
    label: 'Pitch review complete',
    description: 'Get notified when an AI review of your pitch is ready',
    enabled: true,
  },
  {
    label: 'User joined',
    description: 'Get notified when someone accepts your invite',
    enabled: true,
  },
  {
    label: 'Weekly summary',
    description: 'A weekly digest of your pitch performance and activity',
    enabled: false,
  },
]

function FounderSettingsPage() {
  const basePath = useBasePath()
  const { startup } = useStartupContext()
  const startupId = startup.id
  const navigate = useNavigate()
  const activeTab = useActiveTab()

  const { data: members } = useStartupMembersQuery(startupId)
  const { data: invites } = useStartupInvitesQuery(startupId)
  const createInvite = useCreateInviteMutation(startupId)
  const cancelInvite = useCancelInviteMutation(startupId)
  const removeMember = useRemoveStartupMemberMutation(startupId)

  const users: SettingsUser[] = (members ?? []).map((m) => ({
    id: m.id,
    name: m.name ?? m.email ?? 'Unknown',
    email: m.email ?? '',
    initials: getInitials(m.name, m.email),
    role: m.role,
    userId: m.userId,
  }))

  const pendingInvites: SettingsInvite[] = (invites ?? []).map((i) => ({
    id: i.id,
    email: i.email,
    role: i.role,
    token: i.token,
    createdAt: i.createdAt,
  }))

  const handleTabChange = (tab: SettingsTab) => {
    navigate({ to: TAB_ROUTES[tab], params: { id: startupId } })
  }

  return (
    <FounderLayout activeNav="Startup settings">
      <div className="mx-auto max-w-6xl py-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Startup settings
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Manage your workspace settings</p>
        </div>

        <div className="mt-6 flex gap-1 border-b border-border">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => handleTabChange(t.key)}
              className={cn(
                'flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                activeTab === t.key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              <t.icon className="size-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'general' && (
          <div className="mt-6 space-y-8">
            <div>
              <h2 className="text-lg font-medium text-foreground">Access management</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Invite team members and manage who can access this startup workspace.
              </p>
            </div>
            <SettingsView
              hideHeading
              users={users}
              invites={pendingInvites}
              notifications={FOUNDER_NOTIFICATIONS}
              activityLogPath={`${basePath}/activity`}
              onInvite={(email, role) =>
                createInvite.mutate({ body: { email, role }, path: { startupId } })
              }
              onCancelInvite={(inviteId) => cancelInvite.mutate({ path: { startupId, inviteId } })}
              onRemoveMember={(memberId) => removeMember.mutate({ path: { startupId, memberId } })}
              isInviting={createInvite.isPending}
              isOwner
            />
            <InvestorVisibilitySection
              startupId={startupId}
              isStealth={startup.isStealth}
              stealthChoiceMadeAt={startup.stealthChoiceMadeAt}
            />
          </div>
        )}

        {activeTab === 'analytics' && <AnalyticsSettingsPanel startupId={startupId} />}

        {/* The billing page owns its own spacing, so it drops straight in. */}
        {activeTab === 'billing' && <BillingPageContent startupId={startupId} />}
      </div>
    </FounderLayout>
  )
}

function AnalyticsSettingsPanel({ startupId }: { startupId: string }) {
  const { data } = useAnalyticsSettingsQuery(startupId)
  const createExclusion = useCreateAnalyticsExclusionMutation(startupId)
  const deleteExclusion = useDeleteAnalyticsExclusionMutation(startupId)

  const exclusions = data?.exclusions ?? []
  const domains = exclusions.filter((ex) => ex.type === 'domain')
  const emails = exclusions.filter((ex) => ex.type === 'email')

  return (
    <AnalyticsExclusionsPanel
      domains={domains}
      emails={emails}
      onAdd={(type, value) =>
        createExclusion.mutateAsync({
          path: { startupId },
          body: { type, value: value.trim().toLowerCase() },
        })
      }
      onRemove={(exclusionId) => deleteExclusion.mutate({ path: { startupId, exclusionId } })}
      isRemoving={deleteExclusion.isPending}
    />
  )
}
